import { GetStaticProps } from 'next';
import React, { useMemo } from 'react';
import AllProjectsPageComponent from '../../components/elements/AllProjectsPage';
import PageLayout, { MenuProps } from '../../layouts/Page';
import { PROJECT_PAGE } from '../../queries/projects';
import { Project, ProjectsPage } from '../../types/schema';
import { initializeApollo } from '../../utils/apollo';
import { Error, sendError } from '../../utils/error';
import { flatten } from '../../utils/object';

type AllProjectsPageContainerProps = {
  projects: Project[];
  projectsPage: ProjectsPage;
  menus: MenuProps;
};

const AllProjectsPageContainer: React.FC<AllProjectsPageContainerProps> = ({
  projects,
  projectsPage,
  menus,
}) => {
  const [mainProjects, sideProjects] = useMemo(
    () =>
      projects.reduce<[Project[], Project[]]>(
        (prev, curr) => {
          if (curr && curr.type && curr.type === 'main') {
            prev[0].push(curr);
          } else {
            prev[1].push(curr);
          }
          return prev;
        },
        [[], []],
      ),
    [projects],
  );
  return (
    <PageLayout menus={menus} locale="pt" isHeaderTransparent>
      <AllProjectsPageComponent
        bannerImg={(projectsPage?.banner as unknown as { url?: string })?.url as string}
        metaDescription={projectsPage?.metadata?.metaDescription as string}
        metaTitle={projectsPage?.metadata?.metaTitle as string}
        metaImage={
          (projectsPage?.metadata?.metaImage as unknown as { url?: string })?.url as string
        }
        hideAppMastersTitle={projectsPage?.metadata?.hideAppMastersTitle as boolean}
        mainProjects={mainProjects}
        sideProjects={sideProjects}
        language="pt"
      />
    </PageLayout>
  );
};

/**
 * getStaticProps
 */
export const getStaticProps: GetStaticProps = async ({ locale }) => {
  if (locale === 'en') {
    return {
      notFound: true,
    };
  }

  try {
    const apolloClient = initializeApollo();

    const { data } = await apolloClient.query({
      query: PROJECT_PAGE,
      variables: {
        locale,
      },
    });

    const newData = flatten(data);

    return {
      props: {
        projects: newData.allProjects,
        projectsPage: newData.projectsPage,
        menus: {
          header: newData.header,
          technologies: newData.technologies,
          services: newData.services,
          footerPt: newData.footerPt,
          footerEn: newData.footerEn,
        },
      },
    };
  } catch (error) {
    sendError({ error: error as Error, message: 'Error on projetos index' });
    return {
      notFound: true,
    };
  }
};

export default AllProjectsPageContainer;
